import { faList, faListOl, faUserLarge, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogCustomContent,
  Input,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@playcastdotio/Shadcn';
import pcLogo from '../../../assets/playcast_logo-96x96.png';
import { ChangeEvent, ChangeEventHandler, useCallback, useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { faReply, faSliders } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PlaycastContext } from '../PlaycastWrapper';
import { Invitation, LobbyManifest, QueueSettingsBody } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';

type BaseOption = {
  visible?: boolean;
  title: string;
  icon: IconProp;
  tooltip: string;
};

type SwitchOption = BaseOption & {
  type: 'switch';
  isChecked: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

type InputOption = BaseOption & {
  type: 'number';
  max: number;
  value: number;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

type QueueOptions = {
  queueOpen: SwitchOption;
  autoAccept: SwitchOption;
  maxPlayers: InputOption;
  prioritizeNewPlayers: SwitchOption;
  instantConnect: SwitchOption;
};

export const QueueOptionsV2 = () => {
  const context = useContext(PlaycastContext);
  const [maxPlayerValue, setMaxPlayerValue] = useState(context.stateStores.hostSettingsState.maxPlayerCount ?? 4);
  const [maxPlayerValueV2, setMaxPlayerValueV2] = useState(context.stateStores.hostSettingsState.maxPlayerCount ?? 4);

  useEffect(() => {
    setMaxPlayerValue(context.stateStores.hostSettingsState.maxPlayerCount ?? 4);
  }, [context.stateStores.hostSettingsState.maxPlayerCount]);

  useEffect(() => {
    setMaxPlayerValueV2(context.stateStores.hostSettingsState.maxPlayerCount ?? 4);
  }, [context.stateStores.hostSettingsState.maxPlayerCount]);

  const [open, setOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [instantConnect, setInstantConnect] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(constants.USE_EXPERIMENTAL ? maxPlayerValueV2 : maxPlayerValue);
  const [autoAccept, setAutoAccept] = useState(false);
  const [prioritizeNewPlayers, setPrioritizeNewPlayers] = useState(false);
  const [tooltipError, setTooltipError] = useState({ show: false, message: '' });
  const isInstantConnectEnabled = false;

  const handleOptionChange = useCallback(
    (optionKey: keyof QueueOptions, event?: ChangeEvent<HTMLInputElement>) => {
      const maxPlayers = parseInt(event?.target.value || '32');

      if (maxPlayers <= 0) {
        setTooltipError({ show: true, message: 'Max players must be greater than 0' });
      } else if (maxPlayers > 32) {
        setTooltipError({ show: true, message: 'Max guest cannot exceed 32' });
      } else {
        setTooltipError({ ...tooltipError, show: false });
      }

      switch (optionKey) {
        case 'queueOpen':
          setQueueOpen(!queueOpen);
          break;
        case 'instantConnect':
          setInstantConnect(!instantConnect);
          break;
        case 'maxPlayers':
          setMaxPlayers(maxPlayers);
          break;
        case 'autoAccept':
          setAutoAccept(!autoAccept);
          break;
        case 'prioritizeNewPlayers':
          setPrioritizeNewPlayers(!prioritizeNewPlayers);
          break;
      }
    },
    [queueOpen, instantConnect, maxPlayers, autoAccept, prioritizeNewPlayers, tooltipError],
  );

  const populateOptions = useCallback(() => {
    const currentInvitation: Invitation = context.stateStores.invitationStateV2;
    let shouldAutoAccept = false;
    let currentSettings: LobbyManifest['lobbyOptions'] = {
      autoAccept: false,
      canJoin: false,
      maxLobbySize: 4,
      ignoreHostMaxPlayerCount: false,
    };
    const hostSettings = context.stateStores.hostSettingsState;
    let enabled = false;
    let instantConnect = false;

    if (currentInvitation.type === 'lobby') {
      shouldAutoAccept = currentInvitation.manifest.lobbyOptions.autoAccept;
      currentSettings = currentInvitation.manifest.lobbyOptions;
      enabled = currentInvitation.manifest.lobbyOptions.canJoin;
      instantConnect = false;
    }

    setQueueOpen(enabled);
    setInstantConnect(instantConnect);
    setAutoAccept(shouldAutoAccept);
    setMaxPlayers(hostSettings.maxPlayerCount ?? 4);
    setPrioritizeNewPlayers(true); // just default to true for the time being
    return;
  }, [context.stateStores.hostSettingsState, context.stateStores.invitationStateV2]);

  useEffect(() => {
    populateOptions();
  }, [populateOptions]);

  const onOpenChange = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
    populateOptions(); // Refresh options when the dialog is opened
  }, [populateOptions]);

  const handleOptionsApply = useCallback(() => {
    if (constants.USE_EXPERIMENTAL) {
      const invitation: Invitation = context.stateStores.invitationStateV2;
      if (invitation.type !== 'lobby') return;

      invitation.manifest.lobbyOptions.canJoin = queueOpen;
      invitation.manifest.lobbyOptions.maxLobbySize = 32;
      invitation.manifest.lobbyOptions.autoAccept = autoAccept;
      invitation.manifest.lobbyOptions.ignoreHostMaxPlayerCount = false;
      context.targetFunctions.updateLobbySettings(invitation);
      context.targetFunctions.updateHostSettings({
        ...context.stateStores.hostSettingsState,
        maxPlayerCount: maxPlayers,
      });

      setOpen(false);
      return;
    }

    let maxPlayersValue = maxPlayers;
    if (maxPlayersValue <= 0) {
      maxPlayersValue = 1;
    } else if (maxPlayersValue > 32) {
      maxPlayersValue = 32;
    }

    const enabled = queueOpen;
    const settings: QueueSettingsBody = {
      acceptAll: autoAccept,
      maxPlayers: 32,
      prioritizeNewPlayers: prioritizeNewPlayers,
      authRequired: context.stateStores.queueState.settings.authRequired,
      description: prioritizeNewPlayers ? 'This queue prioritizes new players' : '',
    };
    setOpen(false);
    context.targetFunctions.updateQueueSettings(enabled, settings, instantConnect);
    context.targetFunctions.updateHostSettings({
      ...context.stateStores.hostSettingsState,
      maxPlayerCount: maxPlayersValue,
    });
  }, [
    queueOpen,
    instantConnect,
    maxPlayers,
    autoAccept,
    prioritizeNewPlayers,
    context.targetFunctions,
    context.stateStores.hostSettingsState,
  ]);

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event && event.target) {
      event.target.select();
    }
  };

  const handleInputOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event && event.target) {
      let maxPlayersValue = parseInt(event.target.value || '0');
      setTooltipError({ show: false, message: '' });

      if (maxPlayersValue <= 0) {
        maxPlayersValue = 1;
      } else if (maxPlayersValue > 32) {
        maxPlayersValue = 32;
      }

      setMaxPlayers(maxPlayersValue);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <button
        id='lobby-settings-button'
        onClick={() => setOpen(true)}
        className='bg-primary hover:bg-primary/80 active:bg-primary/60 flex w-11 items-center justify-center rounded px-4 py-2 text-white'
        data-testid='lobby-settings-button-test'
      >
        <FontAwesomeIcon icon={faSliders} className='size-6 text-white' />
      </button>
      <DialogCustomContent className='bg-dark text-white sm:max-w-sm'>
        <div className='bg-primary z-50 flex w-full items-center rounded-t-lg shadow-sm'>
          <div className='ml-4 flex shrink-0 items-center gap-x-2'>
            <img src={pcLogo} alt='Logo' className='h-7 w-7' />
            <h1 className='text-md font-headline font-semibold text-white'>Lobby Settings</h1>
          </div>
          <div className='flex-grow' />
          <Button
            size='icon'
            variant='ghost'
            className='h-10 w-9 rounded-t-lg text-gray-300 hover:bg-inherit hover:text-white'
            onClick={onOpenChange}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>
        <div className='flex flex-col gap-4 p-4'>
          <div className='border-primary col-span-1 flex items-center rounded-md border-2 p-2'>
            <div className='relative flex items-center justify-center text-center'>
              <FontAwesomeIcon icon={faList} className={clsx(queueOpen ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')} />
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild id='lobby-open-tooltip-trigger'>
                  <Label htmlFor='queueOpen' className='text-md ml-2 cursor-pointer font-light'>
                    Lobby Open
                  </Label>
                </TooltipTrigger>
                <TooltipContent id='lobby-open-tooltip-content'>
                  <p>When closed, new people cannot join the lobby.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              id='queueOpen'
              checked={queueOpen}
              onCheckedChange={(isChecked: boolean) =>
                handleOptionChange('queueOpen', { target: { checked: isChecked } } as ChangeEvent<HTMLInputElement>)
              }
              className='ml-auto !scale-125 data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-800 [&>span]:bg-white'
            />
          </div>
          {isInstantConnectEnabled && (
            <div className='border-primary col-span-1 flex items-center rounded-md border-2 p-2'>
              <div className='relative flex items-center justify-center text-center'>
                <FontAwesomeIcon icon={faReply} className={clsx(instantConnect ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')} />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild id='instant-connect-tooltip-trigger'>
                    <Label htmlFor='instantConnect' className='text-md ml-2 cursor-pointer font-light'>
                      Instant Connect
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent id='instant-connect-tooltip-content'>
                    <p>When enabled, players skip the waiting screen and join the Playcast immediately.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Switch
                id='instantConnect'
                checked={instantConnect}
                onCheckedChange={(isChecked: boolean) =>
                  handleOptionChange('instantConnect', { target: { checked: isChecked } } as ChangeEvent<HTMLInputElement>)
                }
                className='ml-auto !scale-125 data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-800 [&>span]:bg-white'
              />
            </div>
          )}
          <div className='border-primary col-span-1 flex items-center rounded-md border-2 p-2'>
            <div className='relative flex items-center justify-center text-center'>
              <FontAwesomeIcon icon={faUserLarge} className='h-6 w-6 text-emerald-400' />
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild id='max-players-tooltip-trigger'>
                  <Label htmlFor='maxPlayers' className='text-md ml-2 cursor-pointer font-light'>
                    Max Guest
                  </Label>
                </TooltipTrigger>
                <TooltipContent id='max-players-tooltip-content'>
                  <p>Specifies how many players can be automatically accepted into your Playcast.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip delayDuration={0} open={tooltipError.show}>
                <TooltipTrigger asChild id='max-players-error-tooltip-trigger'>
                  <Input
                    type='number'
                    onChange={(e) => handleOptionChange('maxPlayers', e)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputOnBlur}
                    value={maxPlayers.toString()}
                    className='border-midnight-dark ml-auto max-w-12 border bg-gray-800 p-0 text-right text-lg font-bold drop-shadow-lg focus:outline-none focus:ring-0'
                    placeholder='Playcast Title...'
                  />
                </TooltipTrigger>
                <TooltipContent id='max-players-error-tooltip-content'>
                  <p>{tooltipError.message}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className='border-primary col-span-1 flex items-center rounded-md border-2 p-2'>
            <div className='relative flex items-center justify-center text-center'>
              <FontAwesomeIcon icon={faReply} className={clsx(autoAccept ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')} />
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild id='auto-accept-tooltip-trigger'>
                  <Label htmlFor='autoAccept' className='text-md ml-2 cursor-pointer font-light'>
                    Auto-Accept Players
                  </Label>
                </TooltipTrigger>
                <TooltipContent id='auto-accept-tooltip-content'>
                  <p>
                    When enabled, if there's space, the next player in the lobby automatically receives an invite to join your Playcast.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              id='autoAccept'
              checked={autoAccept}
              onCheckedChange={(isChecked: boolean) =>
                handleOptionChange('autoAccept', { target: { checked: isChecked } } as ChangeEvent<HTMLInputElement>)
              }
              className='ml-auto !scale-125 data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-800 [&>span]:bg-white'
            />
          </div>
          <div className='border-primary col-span-1 flex items-center rounded-md border-2 p-2'>
            <div className='relative flex items-center justify-center text-center'>
              <FontAwesomeIcon icon={faListOl} className={clsx(prioritizeNewPlayers ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')} />
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild id='prioritize-new-players-tooltip-trigger'>
                  <Label htmlFor='prioritizeNewPlayers' className='text-md ml-2 cursor-pointer font-light'>
                    Prioritize New Players
                  </Label>
                </TooltipTrigger>
                <TooltipContent id='prioritize-new-players-tooltip-content'>
                  <p>When enabled people with 0 plays move to the front of the lobby line.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Switch
              id='prioritizeNewPlayers'
              checked={prioritizeNewPlayers}
              onCheckedChange={(isChecked: boolean) =>
                handleOptionChange('prioritizeNewPlayers', { target: { checked: isChecked } } as ChangeEvent<HTMLInputElement>)
              }
              className='ml-auto !scale-125 data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-800 [&>span]:bg-white'
            />
          </div>
        </div>
        <div className='flex flex-1 justify-end p-4'>
          <Button onClick={handleOptionsApply} className='m-0 w-fit bg-amber-400 px-8 hover:bg-amber-400/60'>
            Apply
          </Button>
        </div>
      </DialogCustomContent>
    </Dialog>
  );
};
